import type { Node } from '@xyflow/react';
import type { WorkflowNodeData, EdgePoint } from './workflow-layout';

import { useState, useEffect } from 'react';
import { buildGraph, parseSkillMd } from '@nexus/skill-workflow';

import { alpha } from '@mui/material/styles';
import { Box } from '@mui/material';

import { hasSteps } from './workflow-editor-state';
import { FAMILY_COLOR } from './step-visuals';
import { toCanvasGraph } from './canvas-graph';
import { layoutWorkflowGraph } from './workflow-layout';
import { Workflow } from '../../../components/lucide-icon';

type Kind = WorkflowNodeData['graphNode']['kind'];
type Part = { kind: Kind; family: string | null; x: number; y: number; width: number; height: number };
export type ThumbnailShape = { width: number; height: number; boxes: Part[]; lines: EdgePoint[][] };

const MARGIN = 24;

/** The canvas layout of a skill, in canvas units. Null for a skill without steps. */
export async function thumbnailOf(skillMd: string | null | undefined): Promise<ThumbnailShape | null> {
  if (!skillMd) return null;
  const { workflow } = parseSkillMd(skillMd);
  if (!workflow || !hasSteps(workflow)) return null;
  const { nodes, edges } = await layoutWorkflowGraph(toCanvasGraph(buildGraph(workflow)));

  // Child positions are relative to their group. Edge points are already absolute.
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const origin = (node: Node<WorkflowNodeData> | undefined): EdgePoint => {
    if (!node) return { x: 0, y: 0 };
    const parent = origin(node.parentId ? byId.get(node.parentId) : undefined);
    return { x: parent.x + node.position.x, y: parent.y + node.position.y };
  };
  const boxes: Part[] = nodes.map((node) => ({
    kind: node.data.graphNode.kind,
    family: node.data.graphNode.family,
    ...origin(node),
    width: node.width ?? 0,
    height: node.height ?? 0,
  }));
  const lines = edges.map((e) => e.data?.points ?? []).filter((points) => points.length > 1);

  const xs = [...boxes.flatMap((b) => [b.x, b.x + b.width]), ...lines.flat().map((p) => p.x)];
  const ys = [...boxes.flatMap((b) => [b.y, b.y + b.height]), ...lines.flat().map((p) => p.y)];
  const left = Math.min(...xs) - MARGIN;
  const top = Math.min(...ys) - MARGIN;
  return {
    width: Math.max(...xs) + MARGIN - left,
    height: Math.max(...ys) + MARGIN - top,
    boxes: boxes.map((b) => ({ ...b, x: b.x - left, y: b.y - top })),
    lines: lines.map((points) => points.map((p) => ({ x: p.x - left, y: p.y - top }))),
  };
}

interface WorkflowThumbnailProps {
  skillMd: string | null | undefined;
  width?: number;
  height?: number;
  'data-testid'?: string;
}

/** A small drawing of the skill's canvas, so skills can be told apart at a glance. */
export function WorkflowThumbnail({ skillMd, width = 64, height = 80, ...rest }: WorkflowThumbnailProps) {
  const [shape, setShape] = useState<ThumbnailShape | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    thumbnailOf(skillMd)
      .then((result) => !cancelled && setShape(result))
      .catch(() => !cancelled && setShape(null));
    return () => {
      cancelled = true;
    };
  }, [skillMd]);

  return (
    <Box
      data-testid={rest['data-testid']}
      sx={(theme) => ({
        width,
        height,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: alpha(theme.palette.grey[500], 0.08),
        border: `1px solid ${theme.palette.divider}`,
        color: 'text.disabled',
      })}
    >
      {shape === null && <Workflow size={20} />}
      {shape && (
        <Box
          component="svg"
          viewBox={`0 0 ${shape.width} ${shape.height}`}
          // A very long flow would shrink to a thin line. It shows its top part instead.
          preserveAspectRatio={shape.height / shape.width > (2 * height) / width ? 'xMidYMin slice' : 'xMidYMid meet'}
          sx={{ width: 1, height: 1, display: 'block' }}
          aria-hidden
        >
          {shape.boxes
            .filter((b) => b.kind === 'section' || b.kind === 'repeat')
            .map((b, i) => (
              <Box
                key={`g${i}`}
                component="rect"
                x={b.x}
                y={b.y}
                width={b.width}
                height={b.height}
                rx={16}
                sx={(theme) => ({ fill: 'none', stroke: theme.palette.divider, strokeWidth: 1 })}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          {shape.lines.map((points, i) => (
            <Box
              key={`l${i}`}
              component="polyline"
              points={points.map((p) => `${p.x},${p.y}`).join(' ')}
              sx={(theme) => ({ fill: 'none', stroke: theme.palette.text.disabled, strokeWidth: 1 })}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {shape.boxes
            .filter((b) => b.kind !== 'section' && b.kind !== 'repeat')
            .map((b, i) => (
              <Box
                key={`n${i}`}
                component="rect"
                x={b.x}
                y={b.y}
                width={b.width}
                height={b.height}
                rx={b.kind === 'step' ? 14 : b.height / 2}
                sx={(theme) => {
                  const color =
                    b.kind === 'step' ? FAMILY_COLOR[(b.family ?? 'flow') as keyof typeof FAMILY_COLOR] : null;
                  // Primary is near black on the dark page, so flow steps use grey.
                  return { fill: color && color !== 'primary' ? theme.palette[color].main : theme.palette.grey[500] };
                }}
              />
            ))}
        </Box>
      )}
    </Box>
  );
}
